import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, type Auth } from 'firebase/auth';
import { initializeFirestore, type Firestore } from 'firebase/firestore';
import { getStorage, type FirebaseStorage } from 'firebase/storage';

/**
 * Firebase is initialised from Vite env vars (VITE_FIREBASE_*).
 * The apiKey is NOT a secret — it's a public project identifier. Security comes
 * from Firestore Security Rules + OAuth authorized domains set in the Firebase
 * Console. Never put the backend service account key here.
 */
const firebaseConfig = {
  apiKey:            import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain:        import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId:         import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket:     import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_SENDER_ID,
  appId:             import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId:     import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

export const firebaseApp: FirebaseApp = initializeApp(firebaseConfig);
export const auth: Auth = getAuth(firebaseApp);
// Use initializeFirestore (not getFirestore) so we can set ignoreUndefinedProperties.
// Without this, any write containing an `undefined` value (e.g. avatarUrl when a
// Google account has no profile photo) throws and blocks user-doc creation.
export const db: Firestore = initializeFirestore(firebaseApp, {
  ignoreUndefinedProperties: true,
});
export const storage: FirebaseStorage = getStorage(firebaseApp);

/** Google provider, pre-scoped for email/profile. */
export const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('email');
googleProvider.addScope('profile');
googleProvider.setCustomParameters({ prompt: 'select_account' });
